/* eslint-disable no-console */
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Autocomplete from "SandboxViewerComponent/src/libs/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import APIHelper from "@giscon/utils.api-helper-esri";
import AutocompleteService from "../utils/AutocompleteService";
import { styled } from "@mui/material";

const StyledIcon = styled(LocationOnIcon)(({ theme }) => ({ theme }) => {
  return {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  };
});

const MyComponent = ({
  map,
  mapView,
  minScale,
  minDigits,
  locationSearchURL,
  placeholder,
}) => {
  // set state
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const autocompleteService = new AutocompleteService(
    locationSearchURL,
    minDigits,
    setLoading
  );

  const optionsControl = useRef(null);

  const zoomToBBox = (bbox) => {
    const regex = /BOX\(([\d.]*)\s+([\d.]*)\s*,\s*([\d.]*)\s*([\d.]*)\)/gm;
    const m = regex.exec(bbox);
    let minx;
    let miny;
    let maxx;
    let maxy;
    if (m) {
      minx = parseFloat(m[1]);
      miny = parseFloat(m[2]);
      maxx = parseFloat(m[3]);
      maxy = parseFloat(m[4]);
    }
    const res = APIHelper.projectGeometries(
      {
        geometries: [APIHelper.createExtent(minx, miny, maxx, maxy, 25832)],
      },
      map
    )
      .then(function (geometrien) {
        const zoomEvent = APIHelper.addOnZoomEndListener(
          map,
          () => {
            if (minScale && APIHelper.getMapScale(map, mapView) < minScale) {
              APIHelper.setMapScale(map, mapView, minScale);
            }
            zoomEvent.remove();
          },
          mapView
        );
        APIHelper.zoomToExtent(map, geometrien[0], mapView);
      })
      .catch(function (error) {
        console.error(error);
      });
    return res;
  };
  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        setOptions([]);
        autocompleteService.getPlacePredictions(request, callback);
      }, 200),
    []
  );
  React.useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      loading={loading}
      map={map}
      view={mapView}
      style={{ width: 400 }}
      groupBy={(option) => option.layer}
      // isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      size="small"
      autoComplete
      freeSolo
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue && newValue.bbox) {
          zoomToBBox(newValue.bbox);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          onKeyPress={(event) => {
            if (event.key === "Enter") {
              // console.log("Enter");
              // console.log(optionsControl);
            }
          }}
          style={{
            backgroundColor: "white",
            borderRadius: `${5}px`,
          }}
          {...params}
          label={placeholder}
          variant="outlined"
          fullWidth
        />
      )}
      renderOption={(props, option, state, ownerState) => {
        if (!loading) {
          if (option.msg) {
            return (
              <Grid
                container
                alignItems="center"
                ref={optionsControl}
                {...props}
              >
                {option.msg}
              </Grid>
            );
          }
          const matches =
            option.structured_formatting.main_text_matched_substrings;
          const partsMainText = parse(
            option.structured_formatting.main_text,
            matches
              .filter(
                (match) =>
                  match.offset + match.length <=
                  option.structured_formatting.main_text.length
              )
              .map((match) => [match.offset, match.offset + match.length])
          );
          const partsSecText = parse(
            option.structured_formatting.secondary_text,
            matches
              .filter(
                (match) =>
                  match.offset + match.length >
                  option.structured_formatting.main_text.length
              )
              .map((match) => [
                match.offset -
                  option.structured_formatting.main_text.length -
                  2,
                match.offset -
                  option.structured_formatting.main_text.length +
                  match.length -
                  2,
              ])
          );

          return (
            <Grid container alignItems="center" ref={optionsControl} {...props}>
              <Grid item>
                <StyledIcon />
              </Grid>
              <Grid item xs>
                {partsMainText.map((part, i) => (
                  <span
                    key={`${part.text}_${i}_primary`}
                    style={{ fontWeight: part.highlight ? 700 : 400 }}
                  >
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="textSecondary">
                  {partsSecText.map((part, i) => (
                    <span
                      key={`${part.text}_${i}_secondary`}
                      style={{ fontWeight: part.highlight ? 700 : 400 }}
                    >
                      {part.text}
                    </span>
                  ))}
                </Typography>
              </Grid>
            </Grid>
          );
        }
      }}
    />
  );
};
MyComponent.propTypes = {
  title: PropTypes.string,
};

MyComponent.defaultProps = {
  title: "Hello",
};
export default MyComponent;
